import { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { Button } from './ui.tsx';

/**
 * Shows a freshly issued member code, once.
 *
 * Only a keyed digest of the code is stored, so this is the single moment it
 * can be displayed. The QR encodes a sign-in link rather than the bare code:
 * typing "ram-velvet-otter-42" on a phone keyboard is the one genuinely
 * annoying part of the scheme, and scanning removes it entirely.
 */
export function CodeReveal({
  code,
  label,
  onDone,
}: {
  code: string;
  label: string;
  onDone: () => void;
}) {
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const signInUrl = `${window.location.origin}/?code=${encodeURIComponent(code)}`;

  useEffect(() => {
    let cancelled = false;
    QRCode.toDataURL(signInUrl, {
      errorCorrectionLevel: 'M',
      margin: 2,
      width: 320,
      // Fixed black on white: theme colours would drop contrast below what
      // phone scanners reliably read, especially on the dark themes.
      color: { dark: '#000000', light: '#ffffff' },
    })
      .then((url) => {
        if (!cancelled) setQrDataUrl(url);
      })
      .catch(() => {
        if (!cancelled) setQrDataUrl(null);
      });

    return () => {
      cancelled = true;
    };
  }, [signInUrl]);

  async function copy() {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard access needs a secure context and can be refused. The code
      // is on screen regardless, so this is a convenience, not a requirement.
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="code-reveal-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
    >
      <div className="w-full max-w-sm rounded-xl border border-border bg-surface p-6 shadow-[var(--shadow)]">
        <h2 id="code-reveal-title" className="text-lg font-semibold">
          Code for {label}
        </h2>
        <p className="mt-1 text-sm text-text-muted">
          Only shown once.
        </p>

        <div className="mt-4 rounded-lg border border-border-strong bg-bg p-3 text-center">
          <code className="font-mono text-lg tracking-wide break-all">{code}</code>
        </div>

        {qrDataUrl && (
          <figure className="mt-4 flex flex-col items-center gap-2">
            <img
              src={qrDataUrl}
              alt={`QR code that signs ${label} in`}
              className="w-44 rounded-lg"
            />
            <figcaption className="text-center text-xs text-text-faint">
              Scan to sign in.
            </figcaption>
          </figure>
        )}

        <div className="mt-5 flex gap-2">
          <Button variant="secondary" onClick={copy} className="flex-1">
            {copied ? 'Copied' : 'Copy code'}
          </Button>
          <Button onClick={onDone} className="flex-1">
            Done
          </Button>
        </div>
      </div>
    </div>
  );
}
